import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import CurrencyInput from './controls/CurrencyInput';
import StatusSelector from './controls/StatusSelector';
import BreakdownChart from './display/BreakdownChart';

const { FiCalendar, FiPercent, FiArrowRight, FiClock, FiSettings, FiZap } = FiIcons;

const HOURLY_HOURS = 151.67;
// Source : Décret n° 2025-1451 du 29 décembre 2025 — SMIC au 1er janvier 2026
const SMIC_HOURLY_GROSS = 12.02;   // 12,02 € brut/h → 9,52 € net/h
const SMIC_MONTHLY_GROSS = 1823.03; // 1 823,03 € brut/mois → 1 443,11 € net/mois

const RATES = {
  'non-cadre': 0.22,
  'cadre': 0.25,
  'public': 0.15,
  'portage': 0.50,
};

export default function SalaryCalculator() {
  const [annualGrossBase, setAnnualGrossBase] = useState(36000);
  const [status, setStatus] = useState('non-cadre');
  const [months, setMonths] = useState(12);
  const [taxRate, setTaxRate] = useState(0);
  const [workTime, setWorkTime] = useState(100);
  const [smicFlash, setSmicFlash] = useState(false);

  const rate = RATES[status];
  const workFactor = workTime / 100;
  const actualAnnualGross = annualGrossBase * workFactor;
  const actualAnnualNet = actualAnnualGross * (1 - rate);
  const monthlyGross = actualAnnualGross / months;
  const monthlyNet = actualAnnualNet / months;
  const hourlyGross = (annualGrossBase / 12) / HOURLY_HOURS;
  const hourlyNet = hourlyGross * (1 - rate);
  const monthlyTaxAmount = monthlyNet * (taxRate / 100);
  const monthlyNetAfterTax = monthlyNet - monthlyTaxAmount;

  const applySmic = () => {
    const safeWorkFactor = workFactor > 0 ? workFactor : 1;
    const newBase = (SMIC_MONTHLY_GROSS * months) / safeWorkFactor;
    setAnnualGrossBase(newBase);
    setSmicFlash(true);
    setTimeout(() => setSmicFlash(false), 600);
  };

  const updateSalary = (type, val) => {
    if (val < 0) val = 0;
    let newAnnualGrossBase = 0;
    const safeWorkFactor = workFactor > 0 ? workFactor : 1;
    switch (type) {
      case 'annualGross': newAnnualGrossBase = val / safeWorkFactor; break;
      case 'annualNet': newAnnualGrossBase = (val / (1 - rate)) / safeWorkFactor; break;
      case 'monthlyGross': newAnnualGrossBase = (val * months) / safeWorkFactor; break;
      case 'monthlyNet': newAnnualGrossBase = ((val * months) / (1 - rate)) / safeWorkFactor; break;
      case 'hourlyGross': newAnnualGrossBase = val * HOURLY_HOURS * 12; break;
      case 'hourlyNet': newAnnualGrossBase = (val / (1 - rate)) * HOURLY_HOURS * 12; break;
      default: break;
    }
    setAnnualGrossBase(newAnnualGrossBase);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">

      {/* HERO RESULT */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900 rounded-3xl p-5 shadow-xl relative overflow-hidden"
      >
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1">
            <h3 className="text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mb-1">
              Net après impôts ({workTime}% temps de travail)
            </h3>
            <div className="flex items-baseline gap-3 text-white">
              <span className="text-5xl font-black tracking-tight">
                {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(monthlyNetAfterTax)}
              </span>
              <span className="text-2xl font-medium text-emerald-400">
                €<span className="text-base text-slate-500 ml-1">/mois</span>
              </span>
            </div>
            {taxRate > 0 && (
              <p className="text-slate-400 text-xs mt-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                Impôt prélevé : <strong className="text-slate-200">{new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(monthlyTaxAmount)} €</strong>
              </p>
            )}
          </div>
          <div className="w-full md:w-72 pt-3 md:pt-0 border-t md:border-t-0 md:border-l border-slate-800 md:pl-6">
            <BreakdownChart gross={monthlyGross} net={monthlyNet} tax={monthlyTaxAmount} />
          </div>
        </div>
      </motion.div>

      {/* SALARY + SETTINGS SIDE BY SIDE */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

        {/* SALARY INPUTS */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-7 bg-white p-5 rounded-3xl shadow-sm ring-1 ring-slate-900/5"
        >
          {/* Header row with SMIC button */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center mb-4 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-center font-bold text-slate-400 uppercase tracking-widest text-[10px]">Brut</h2>
              <motion.button
                onClick={applySmic}
                animate={smicFlash ? { scale: [1, 1.12, 1] } : {}}
                transition={{ duration: 0.3 }}
                title={`SMIC 2026 : ${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(SMIC_MONTHLY_GROSS)} € brut/mois (${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(1443.11)} € net) — ${SMIC_HOURLY_GROSS.toFixed(2).replace('.', ',')} €/h brut (9,52 € net) — en vigueur au 1er janvier 2026`}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 hover:border-indigo-400 text-indigo-600 text-[9px] font-bold uppercase tracking-wide transition-all cursor-pointer"
              >
                <SafeIcon icon={FiZap} className="text-[9px]" />
                SMIC 2026
              </motion.button>
            </div>
            <div className="w-6" />
            <h2 className="text-center font-bold text-emerald-500 uppercase tracking-widest text-[10px]">Net</h2>
          </div>

          <div className="space-y-3">
            {/* Monthly */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end relative py-1">
              <div className="absolute -left-5 top-1/2 -translate-y-1/2 w-1 h-10 bg-indigo-500 rounded-r-md" />
              <CurrencyInput label={`Mensuel (${workTime}%)`} value={monthlyGross} onChange={(v) => updateSalary('monthlyGross', v)} autoFocus />
              <div className="pb-4 text-slate-300"><SafeIcon icon={FiArrowRight} /></div>
              <CurrencyInput label={`Mensuel (${workTime}%)`} value={monthlyNet} onChange={(v) => updateSalary('monthlyNet', v)} />
            </div>
            {/* Annual */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end py-1">
              <CurrencyInput label={`Annuel (${workTime}%)`} value={actualAnnualGross} onChange={(v) => updateSalary('annualGross', v)} />
              <div className="pb-4 text-slate-300"><SafeIcon icon={FiArrowRight} /></div>
              <CurrencyInput label={`Annuel (${workTime}%)`} value={actualAnnualNet} onChange={(v) => updateSalary('annualNet', v)} />
            </div>
            {/* Hourly */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end py-1 opacity-80 hover:opacity-100 transition-opacity">
              <CurrencyInput label="Taux Horaire" value={hourlyGross} decimals={2} onChange={(v) => updateSalary('hourlyGross', v)} />
              <div className="pb-4 text-slate-200"><SafeIcon icon={FiArrowRight} /></div>
              <CurrencyInput label="Taux Horaire" value={hourlyNet} decimals={2} onChange={(v) => updateSalary('hourlyNet', v)} />
            </div>
          </div>
        </motion.div>

        {/* SETTINGS SIDEBAR */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="bg-white p-5 rounded-3xl shadow-sm ring-1 ring-slate-900/5 space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-3">
              <SafeIcon icon={FiSettings} className="text-slate-400" />
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Paramètres</h3>
            </div>

            <StatusSelector status={status} setStatus={setStatus} />

            {/* Work Time */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-tighter flex items-center gap-1.5">
                  <SafeIcon icon={FiClock} className="text-indigo-400" />
                  Temps : {workTime}%
                </label>
              </div>
              <input type="range" min="10" max="100" step="5" value={workTime}
                onChange={(e) => setWorkTime(parseFloat(e.target.value))}
                className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
            </div>

            {/* Tax Rate */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-tighter flex items-center gap-1.5">
                  <SafeIcon icon={FiPercent} className="text-indigo-400" />
                  Impôts : {taxRate}%
                </label>
              </div>
              <input type="range" min="0" max="40" step="0.5" value={taxRate}
                onChange={(e) => setTaxRate(parseFloat(e.target.value))}
                className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
            </div>

            {/* Months */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-tighter">
                <SafeIcon icon={FiCalendar} className="text-indigo-400" />
                Périodicité
              </div>
              <select value={months} onChange={(e) => setMonths(parseInt(e.target.value))}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-sm text-slate-700 font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              >
                <option value={12}>12 mois</option>
                <option value={13}>13 mois</option>
                <option value={14}>14 mois</option>
              </select>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
}